import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContractCgvStatus } from '@core/models/contract-cgv-status.model';
import { ContractSector } from '@core/models/contract-sector.model';
import { ContractCgvStatusService } from '@core/services/contract-cgv-status/contract-cgv-status.service';
import { ContractSectorService } from '@core/services/contract-sector/contract-sector.service';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
standalone: false,
  selector: 'app-contract-advertiser-informations',
  templateUrl: './contract-advertiser-informations.component.html',
  styleUrls: ['./contract-advertiser-informations.component.scss'],
})
export class ContractAdvertiserInformationsComponent implements OnInit {
  @Input() contractItemInformations: any;

  advertiserGroupForm: FormGroup;

  // Autocomplete data
  isContractCgvStatusLoading = false;
  advertiserStatusList: ContractCgvStatus[] = [];
  filteredAdvertiserStatusList: Observable<ContractCgvStatus[]>;
  isContractSectorLoading = false;
  contractSectorList: ContractSector[] = [];
  filteredContractSectorList: Observable<ContractSector[]>;

  componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private fb: FormBuilder,
    private contractCgvStatusService: ContractCgvStatusService,
    private contractSectorService: ContractSectorService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initAdvertiserForm();
    this.getContractCgvStatus();
    this.getContractSector();
  }

  public cancelFormsModification() {
    this.initForm();
    this.initAdvertiserForm();
  }

  public isFormValid(): boolean {
    return this.advertiserGroupForm.valid;
  }

  public getFormData() {
    return this.advertiserGroupForm.value;
  }

  public markAllAsTouched() {
    this.advertiserGroupForm.markAllAsTouched();
  }

  protected initForm() {
    this.advertiserGroupForm = this.fb.group({
      advertiserStatus: [null, Validators.required],
      sector: [null, Validators.required],
    });
  }

  private initAdvertiserForm() {
    if (!this.contractItemInformations.advertiserStatus || !this.contractItemInformations.sector) {
      return;
    }

    let contractAdvertiserStatus = new ContractCgvStatus(this.contractItemInformations.advertiserStatus);
    let contractSector = new ContractSector(this.contractItemInformations.sector);

    this.advertiserGroupForm.patchValue({
      advertiserStatus: contractAdvertiserStatus,
      sector: contractSector,
    });
  }

  // --- Load autocomplete data ---

  // --- advertiser status ---

  private getContractCgvStatus() {
    this.isContractCgvStatusLoading = true;

    this.contractCgvStatusService
      .getList({})
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        advertiserStatusList => {
          this.isContractCgvStatusLoading = false;
          this.advertiserStatusList = advertiserStatusList;
          this.initFilteredAdvertiserStatusList();
        },
        error => {
          this.isContractCgvStatusLoading = false;
        }
      );
  }

  private initFilteredAdvertiserStatusList() {
    if (!this.isContractCgvStatusLoading) {
      this.filteredAdvertiserStatusList = this.advertiserGroupForm.get('advertiserStatus').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterCgvStatus(label) : this.advertiserStatusList.slice()))
      );
    }
  }

  private _filterCgvStatus(value: string = ''): ContractCgvStatus[] {
    const filterValue = value.toLowerCase();
    return this.advertiserStatusList.filter(
      contractCgvStatus =>
        contractCgvStatus.id.toString().toLowerCase().includes(filterValue) || contractCgvStatus.name.toLowerCase().includes(filterValue)
    );
  }

  // --- contract sector ---

  private getContractSector() {
    this.isContractSectorLoading = true;

    this.contractSectorService
      .getList({})
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractSectorList => {
          this.isContractSectorLoading = false;
          this.contractSectorList = contractSectorList;
          this.initFilteredContractSectorList();
        },
        error => {
          this.isContractSectorLoading = false;
        }
      );
  }

  private initFilteredContractSectorList() {
    if (!this.isContractSectorLoading) {
      this.filteredContractSectorList = this.advertiserGroupForm.get('sector').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterContractSector(label) : this.contractSectorList.slice()))
      );
    }
  }

  private _filterContractSector(value: string = ''): ContractSector[] {
    const filterValue = value.toLowerCase();
    return this.contractSectorList.filter(
      contractSector =>
        contractSector.id.toString().toLowerCase().includes(filterValue) || contractSector.name.toLowerCase().includes(filterValue)
    );
  }

  // --- Utility ---

  private _getLabel(value: string | ContractCgvStatus | ContractSector): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  public displayAutoCompleteData(data: any): string {
    if (!data) return '';
    return typeof data === 'string' ? data : data.name + ' (' + data.id + ')';
  }

  // --- On Destroy ---

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
